import React, { useState, useEffect, useCallback } from 'react';
import { PencilIcon, CheckIcon, XMarkIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import presupuestoService from '../services/presupuesto.service';
import rubrosPresupuestalesService from '../services/rubros-presupuestales.service';
import { RubroPresupuestal } from '../types';
import { formatCurrency } from '../utils/currency';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

interface RubroConPresupuesto {
  rubro: RubroPresupuestal;
  totalAnual: number;
  totalEjecutado: number;
  meses: {
    mes: number;
    montoPresupuestado: number;
    montoEjecutado: number;
    presupuestoId?: number;
  }[];
  expandido: boolean;
  editandoTotal: boolean;
  editandoMes: number | null;
}

const Presupuesto: React.FC = () => {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [tipo, setTipo] = useState<'ingreso' | 'gasto'>('ingreso');
  const [rubros, setRubros] = useState<RubroConPresupuesto[]>([]);
  const [loading, setLoading] = useState(true);
  const [montoEditando, setMontoEditando] = useState('');
  const [guardando, setGuardando] = useState(false);

  const fetchDatos = useCallback(async () => {
    try {
      setLoading(true);
      
      // Obtener rubros activos del tipo seleccionado
      const rubrosActivos = await rubrosPresupuestalesService.getRubrosActivos(tipo);
      
      // Obtener presupuestos del año
      const presupuestosAnio = await presupuestoService.getPresupuestoPorAnio(anio);
      
      // Combinar datos
      const rubrosConPresupuesto: RubroConPresupuesto[] = rubrosActivos.map(rubro => {
        const presupuestoRubro = presupuestosAnio.find(p => p.rubro.id === rubro.id);
        
        return {
          rubro,
          totalAnual: presupuestoRubro?.totalPresupuestado || 0,
          totalEjecutado: presupuestoRubro?.totalEjecutado || 0,
          meses: presupuestoRubro?.meses || Array.from({ length: 12 }, (_, i) => ({
            mes: i + 1,
            montoPresupuestado: 0,
            montoEjecutado: 0
          })),
          expandido: false,
          editandoTotal: false,
          editandoMes: null
        };
      });
      
      setRubros(rubrosConPresupuesto);
    } catch (error) {
      console.error('Error fetching datos:', error);
      alert('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [anio, tipo]);

  useEffect(() => {
    fetchDatos();
  }, [fetchDatos]);

  const toggleExpandir = (index: number) => {
    setRubros(prev => prev.map((r, i) => 
      i === index ? { ...r, expandido: !r.expandido } : r
    ));
  };

  const handleEditarTotal = (index: number) => {
    setRubros(prev => prev.map((r, i) => 
      i === index ? { ...r, editandoTotal: true } : r
    ));
    setMontoEditando(rubros[index].totalAnual.toString());
  };

  const handleCancelarEdicionTotal = (index: number) => {
    setRubros(prev => prev.map((r, i) => 
      i === index ? { ...r, editandoTotal: false } : r
    ));
    setMontoEditando('');
  };

  const handleGuardarTotal = async (index: number) => {
    try {
      setGuardando(true);
      const rubro = rubros[index];
      const nuevoTotal = parseFloat(montoEditando) || 0;
      
      // Calcular distribución mensual (dividir en 12) con precisión
      // Usar enteros para evitar problemas de punto flotante
      const totalCentavos = Math.round(nuevoTotal * 100);
      const montoPorMesCentavos = Math.floor(totalCentavos / 12);
      const montoPorMes = montoPorMesCentavos / 100;
      
      // Calcular el residuo para el último mes
      const residuoCentavos = totalCentavos - (montoPorMesCentavos * 12);
      const montoUltimoMes = (montoPorMesCentavos + residuoCentavos) / 100;
      
      // Guardar cada mes
      for (let mes = 1; mes <= 12; mes++) {
        const mesData = rubro.meses.find(m => m.mes === mes);
        const montoMes = mes === 12 ? montoUltimoMes : montoPorMes;
        
        if (mesData?.presupuestoId) {
          // Actualizar existente
          await presupuestoService.updatePresupuesto(mesData.presupuestoId, {
            montoPresupuestado: montoMes
          });
        } else if (montoMes > 0) {
          // Crear nuevo
          await presupuestoService.createPresupuesto({
            rubroId: rubro.rubro.id,
            anio,
            mes,
            montoPresupuestado: montoMes
          });
        }
      }
      
      await fetchDatos();
      setRubros(prev => prev.map((r, i) => 
        i === index ? { ...r, editandoTotal: false } : r
      ));
      setMontoEditando('');
    } catch (error) {
      console.error('Error guardando total:', error);
      alert('Error al guardar presupuesto');
    } finally {
      setGuardando(false);
    }
  };

  const handleEditarMes = (index: number, mes: number) => {
    setRubros(prev => prev.map((r, i) => 
      i === index ? { ...r, editandoMes: mes } : r
    ));
    const mesData = rubros[index].meses.find(m => m.mes === mes);
    setMontoEditando(mesData?.montoPresupuestado.toString() || '0');
  };

  const handleCancelarEdicionMes = (index: number) => {
    setRubros(prev => prev.map((r, i) => 
      i === index ? { ...r, editandoMes: null } : r
    ));
    setMontoEditando('');
  };

  const handleGuardarMes = async (index: number, mes: number) => {
    try {
      setGuardando(true);
      const rubro = rubros[index];
      const nuevoMonto = parseFloat(montoEditando) || 0;
      const mesData = rubro.meses.find(m => m.mes === mes);
      
      if (mesData?.presupuestoId) {
        // Actualizar existente
        await presupuestoService.updatePresupuesto(mesData.presupuestoId, {
          montoPresupuestado: nuevoMonto
        });
      } else if (nuevoMonto > 0) {
        // Crear nuevo
        await presupuestoService.createPresupuesto({
          rubroId: rubro.rubro.id,
          anio,
          mes,
          montoPresupuestado: nuevoMonto
        });
      }
      
      await fetchDatos();
      setRubros(prev => prev.map((r, i) => 
        i === index ? { ...r, editandoMes: null } : r
      ));
      setMontoEditando('');
    } catch (error) {
      console.error('Error guardando mes:', error);
      alert('Error al guardar presupuesto mensual');
    } finally {
      setGuardando(false);
    }
  };

  const calcularTotales = () => {
    return rubros.reduce((acc, r) => ({
      presupuestado: acc.presupuestado + r.totalAnual,
      ejecutado: acc.ejecutado + r.totalEjecutado
    }), { presupuestado: 0, ejecutado: 0 });
  };

  const totales = calcularTotales();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Cargando presupuesto...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Presupuesto Anual</h1>
          <p className="mt-1 text-sm text-gray-500">
            Ingresa el presupuesto anual por rubro. Opcionalmente, expande para distribuir mensualmente.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Año
            </label>
            <select
              value={anio}
              onChange={(e) => setAnio(parseInt(e.target.value))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              {[2024, 2025, 2026, 2027, 2028].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <div className="flex space-x-4">
              <button
                onClick={() => setTipo('ingreso')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md ${
                  tipo === 'ingreso'
                    ? 'bg-green-100 text-green-800 border-2 border-green-500'
                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                Ingresos
              </button>
              <button
                onClick={() => setTipo('gasto')}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md ${
                  tipo === 'gasto'
                    ? 'bg-red-100 text-red-800 border-2 border-red-500'
                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                }`}
              >
                Gastos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Rubros */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rubro
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Presupuesto Anual
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ejecutado
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                % Ejecución
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rubros.map((item, index) => (
              <React.Fragment key={item.rubro.id}>
                {/* Fila principal */}
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleExpandir(index)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {item.expandido ? (
                        <ChevronDownIcon className="h-5 w-5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.rubro.codigo}</div>
                    <div className="text-sm text-gray-500">{item.rubro.nombre}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {item.editandoTotal ? (
                      <div className="flex items-center justify-end space-x-2">
                        <input
                          type="number"
                          value={montoEditando}
                          onChange={(e) => setMontoEditando(e.target.value)}
                          className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-right"
                          autoFocus
                          disabled={guardando}
                        />
                        <button
                          onClick={() => handleGuardarTotal(index)}
                          disabled={guardando}
                          className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          <CheckIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleCancelarEdicionTotal(index)}
                          disabled={guardando}
                          className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(item.totalAnual)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className={`text-sm font-medium ${
                      item.totalEjecutado >= item.totalAnual ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {formatCurrency(item.totalEjecutado)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm text-gray-900">
                      {item.totalAnual > 0 
                        ? `${Math.round((item.totalEjecutado / item.totalAnual) * 100)}%`
                        : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {!item.editandoTotal && (
                      <button
                        onClick={() => handleEditarTotal(index)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>

                {/* Fila expandida con distribución mensual */}
                {item.expandido && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 bg-gray-50">
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700 mb-3">
                          Distribución Mensual
                          {item.totalAnual > 0 && (
                            <span className="ml-2 text-xs text-gray-500">
                              (Total: {formatCurrency(item.meses.reduce((sum, m) => sum + m.montoPresupuestado, 0))})
                              {item.meses.reduce((sum, m) => sum + m.montoPresupuestado, 0) !== item.totalAnual && (
                                <span className="ml-2 text-orange-600">
                                  ⚠️ No coincide con el total anual
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                          {item.meses.map((mes) => (
                            <div key={mes.mes} className="border border-gray-200 rounded-lg p-3 bg-white">
                              <div className="text-xs font-medium text-gray-500 mb-1">
                                {MESES[mes.mes - 1]}
                              </div>
                              {item.editandoMes === mes.mes ? (
                                <div className="space-y-2">
                                  <input
                                    type="number"
                                    value={montoEditando}
                                    onChange={(e) => setMontoEditando(e.target.value)}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    autoFocus
                                    disabled={guardando}
                                  />
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => handleGuardarMes(index, mes.mes)}
                                      disabled={guardando}
                                      className="flex-1 p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                    >
                                      <CheckIcon className="h-3 w-3 mx-auto" />
                                    </button>
                                    <button
                                      onClick={() => handleCancelarEdicionMes(index)}
                                      disabled={guardando}
                                      className="flex-1 p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                                    >
                                      <XMarkIcon className="h-3 w-3 mx-auto" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {mes.montoPresupuestado > 0 ? formatCurrency(mes.montoPresupuestado) : '-'}
                                  </div>
                                  {mes.montoEjecutado > 0 && (
                                    <div className={`text-xs ${
                                      mes.montoEjecutado >= mes.montoPresupuestado
                                        ? 'text-green-600'
                                        : 'text-orange-600'
                                    }`}>
                                      {formatCurrency(mes.montoEjecutado)}
                                    </div>
                                  )}
                                  <button
                                    onClick={() => handleEditarMes(index, mes.mes)}
                                    className="text-xs text-primary-600 hover:text-primary-800"
                                  >
                                    Editar
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot className="bg-gray-100">
            <tr>
              <td colSpan={2} className="px-6 py-3 text-sm font-bold text-gray-900">
                TOTAL {tipo.toUpperCase()}
              </td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                {formatCurrency(totales.presupuestado)}
              </td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                {formatCurrency(totales.ejecutado)}
              </td>
              <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                {totales.presupuestado > 0 
                  ? `${Math.round((totales.ejecutado / totales.presupuestado) * 100)}%`
                  : '-'}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Leyenda */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">Cómo usar:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Presupuesto Anual:</strong> Haz clic en el ícono de lápiz para ingresar el total anual del rubro</li>
          <li>• <strong>Distribución Automática:</strong> Al guardar el total, se divide automáticamente en 12 meses</li>
          <li>• <strong>Ajuste Mensual:</strong> Expande el rubro (flecha) para ver y editar la distribución mensual</li>
          <li>• <strong>Validación:</strong> El sistema te alerta si la suma mensual no coincide con el total anual</li>
          <li>• <strong>Ejecutado:</strong> Se calcula automáticamente desde los pagos/gastos reales registrados</li>
        </ul>
      </div>
    </div>
  );
};

export default Presupuesto;
